/**
 * Application Constants
 * Centralized configuration for the LMS frontend
 */

// Application info — VERSION is read from package.json at build time
// via REACT_APP_VERSION env var (set in package.json scripts or .env)
export const APP_INFO = {
  NAME: 'LMS',
  VERSION: process.env.REACT_APP_VERSION || '1.0.1',
  DESCRIPTION: 'Learning Management System',
  BRAND: 'Shuhrat'
};

// Changelog data
export const CHANGELOG = [
  {
    version: '1.0.1',
    date: '2026-03-03',
    features: [
      'version.entries.1_0_1.features.offlineModeDelayedTransition',
      'version.entries.1_0_1.features.profilePhotosCachedLocally',
    ],
    bugFixes: [
      'version.entries.1_0_1.bugFixes.lessonScheduleConflictRequest',
      'version.entries.1_0_1.bugFixes.logoutTokenDeletion',
      'version.entries.1_0_1.bugFixes.cachedDataClearedOnLogout',
    ],
  },
  {
    version: '1.0.0',
    date: '2026-02-15',
    features: [
      'version.entries.1_0_0.features.initialRelease',
      // 'version.entries.1_0_0.features.adminDashboardHealthMonitoring',
      // 'version.entries.1_0_0.features.userManagement',
      // 'version.entries.1_0_0.features.courseAndGroupManagement',
      // 'version.entries.1_0_0.features.lessonSchedulingConflictDetection',
      'version.entries.1_0_0.features.realtimeMessaging',
      // 'version.entries.1_0_0.features.mockExamManagement',
      'version.entries.1_0_0.features.multilanguageSupport',
      // 'version.entries.1_0_0.features.pwaOfflineSupport',
    ],
    bugFixes: [],
  },
];

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_DATA: 'lms-auth-data',
  REMEMBER_ME: 'lms-remember-me',
  THEME: 'lms-theme',
  LANGUAGE: 'lms-language',
  SIDEBAR_COLLAPSED: 'lms-sidebar-collapsed',
  PUSH_ENABLED: 'lms-push-enabled',
  VAPID_PUBLIC_KEY: 'lms-vapid-public-key',
};

// User roles
export const ROLES = {
  ADMIN: 'ADMIN',
  TEACHER: 'TEACHER',
  STUDENT: 'STUDENT',
  PARENT: 'PARENT',
};

// API request timeouts (in ms)
export const TIMEOUTS = {
  DEFAULT: 100000,
  UPLOAD: 300000,
  DOWNLOAD: 300000,
};

// Pagination defaults
export const PAGINATION = {
  DEFAULT_PAGE: 0,
  DEFAULT_SIZE: 10,
  PAGE_SIZE_OPTIONS: ['10', '20', '50', '100'],
};

// Days of week
export const DAYS_OF_WEEK = [
  { value: 'MONDAY', label: 'Monday', short: 'Mon' },
  { value: 'TUESDAY', label: 'Tuesday', short: 'Tue' },
  { value: 'WEDNESDAY', label: 'Wednesday', short: 'Wed' },
  { value: 'THURSDAY', label: 'Thursday', short: 'Thu' },
  { value: 'FRIDAY', label: 'Friday', short: 'Fri' },
  { value: 'SATURDAY', label: 'Saturday', short: 'Sat' },
  { value: 'SUNDAY', label: 'Sunday', short: 'Sun' },
];

// Homework/Task status
export const TASK_STATUS = {
  PENDING: 'PENDING',
  SUBMITTED: 'SUBMITTED',
  EVALUATED: 'EVALUATED',
  LATE: 'LATE',
};

// Exam status
export const EXAM_STATUS = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
};

// Update types
export const UPDATE_TYPES = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  IMPORTANT: 'IMPORTANT',
  REPORT: 'REPORT',
};

// Breakpoints for responsive design
export const BREAKPOINTS = {
  MOBILE: 768,
  TABLET: 1024,
  DESKTOP: 1280,
};

// WebSocket connection settings.
// Tuned for high-latency / unreliable mobile networks: the previous 4s heartbeats
// caused slow connections to be torn down and reconnected repeatedly.
export const WEBSOCKET = {
  RECONNECT_DELAY: 2000,        // initial reconnect delay; grows exponentially from here
  MAX_RECONNECT_DELAY: 30000,   // upper bound for the exponential backoff
  CONNECTION_TIMEOUT: 30000,    // abort a stalled handshake and retry instead of hanging forever
  HEARTBEAT_INCOMING: 25000,    // expect a server heartbeat every 25s (was 4s — too aggressive for mobile)
  HEARTBEAT_OUTGOING: 25000,    // send a heartbeat every 25s
  HEARTBEAT_TOLERANCE: 3,       // extra slack before a missed heartbeat is treated as a dead link
};

// Message types for notifications
export const MESSAGE_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// File size limits (in bytes)
export const FILE_LIMITS = {
  MAX_UPLOAD_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_IMAGE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  ALLOWED_DOC_TYPES: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
};
